import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Search, ChevronRight } from "lucide-react";
import { StatusPill } from "@/pages/ProductRequest";

/**
 * Service Head / Admin page: review CCR-submitted product requests.
 *
 * Tabs: Pending (default) · Needs Correction · Approved · Rejected · All.
 * Row actions on Pending/Needs Correction: Approve, Send Back for Correction, Reject.
 * Reject + Send Back require a non-empty remarks. Approve also propagates the
 * warranty fields into the warranty_rules collection (backend handles this).
 */
export default function ProductApproval() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);   // currently-selected row for review panel
  const [actionDialog, setActionDialog] = useState(null);  // { product, action }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/products", { params: { status: tab } });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((p) => [p.name, p.brand, p.model, p.product_category, p.category, p.created_by]
      .some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-5" data-testid="product-approval-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Product Approval</h1>
        <p className="text-sm text-slate-500 mt-1">
          Review new product requests submitted by Customer Care. Approve to publish to complaint registration and auto-create the warranty rule.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setActive(null); }}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="pending" data-testid="approval-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="needs_correction" data-testid="approval-tab-needs-correction">Needs Correction</TabsTrigger>
          <TabsTrigger value="approved" data-testid="approval-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approval-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="approval-tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-3">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card className="lg:col-span-3">
              <CardContent className="p-0">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
                  <div className="text-sm text-slate-600">{filtered.length} record(s) {loading && "· loading…"}</div>
                  <div className="relative">
                    <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
                    <Input className="pl-7 max-w-xs h-8 text-sm" placeholder="Search name/brand/model…"
                           value={search} onChange={(e) => setSearch(e.target.value)}
                           data-testid="approval-search" />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <Th>Status</Th>
                        <Th>Brand</Th>
                        <Th>Product</Th>
                        <Th>Model</Th>
                        <Th>Category</Th>
                        <Th>Submitted by</Th>
                        <Th align="right">Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr><td colSpan={7} className="py-12 text-center text-slate-400">No products in this state.</td></tr>
                      )}
                      {filtered.map((p) => (
                        <tr key={p.id}
                            className={`cursor-pointer hover:bg-slate-50 ${active?.id === p.id ? "bg-slate-50" : ""}`}
                            onClick={() => setActive(p)}
                            data-testid={`approval-row-${p.id}`}>
                          <td><StatusPill status={p.status || "approved"} /></td>
                          <td>{p.brand || "—"}</td>
                          <td className="font-medium">{p.name}</td>
                          <td className="font-mono text-xs">{p.model || "—"}</td>
                          <td className="text-xs">{p.product_category || p.category || "—"}</td>
                          <td className="text-xs text-slate-600">{p.created_by || "—"}</td>
                          <td className="text-right">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setActive(p); }}
                                    data-testid={`approval-open-${p.id}`}>
                              <ChevronRight size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                {!active ? (
                  <div className="text-sm text-slate-400 text-center py-12">
                    Select a product on the left to review its details.
                  </div>
                ) : (
                  <ReviewPanel product={active}
                               onAction={(action) => setActionDialog({ product: active, action })} />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {actionDialog && (
        <ActionDialog
          product={actionDialog.product}
          action={actionDialog.action}
          onClose={() => setActionDialog(null)}
          onDone={() => { setActionDialog(null); setActive(null); load(); }}
        />
      )}
    </div>
  );
}

function ReviewPanel({ product, onAction }) {
  const reviewable = ["pending", "needs_correction"].includes(product.status);
  const Coverage = ({ label, k }) => {
    const v = product[k];
    if (v === null || v === undefined || v === "") return null;
    return (
      <div className="flex items-center justify-between text-xs py-1 border-b border-slate-100">
        <span className="text-slate-500">{label}</span>
        <span className="font-mono text-slate-800">{v} mo</span>
      </div>
    );
  };
  return (
    <div className="space-y-3" data-testid="review-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{product.brand}</div>
          <div className="font-heading text-lg font-bold text-slate-900">{product.name}</div>
          <div className="text-xs font-mono text-slate-600">{product.model || "—"}</div>
        </div>
        <StatusPill status={product.status || "approved"} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <Info label="Category" value={product.product_category || product.category} />
        <Info label="Size" value={product.product_size} />
        <Info label="Other model" value={product.other_model} />
        <Info label="Submitted by" value={product.created_by} />
      </div>

      {product.specifications && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Specifications</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{product.specifications}</div>
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Warranty Coverage</div>
        <Coverage label="Comprehensive" k="comprehensive_months" />
        <Coverage label="Compressor" k="compressor_months" />
        <Coverage label="Motor" k="motor_months" />
        <Coverage label="Parts" k="parts_months" />
        <Coverage label="Service" k="service_months" />
        {!product.comprehensive_months && !product.compressor_months && !product.motor_months
         && !product.parts_months && !product.service_months && (
          <div className="text-xs text-slate-400">No coverage period set.</div>
        )}
        {product.warranty_clause && (
          <div className="mt-2 text-xs text-slate-700 bg-slate-50 border rounded p-2 whitespace-pre-wrap">
            {product.warranty_clause}
          </div>
        )}
      </div>

      {product.review_remarks && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">Last review remarks</div>
          <div className="text-xs text-amber-900 whitespace-pre-wrap">{product.review_remarks}</div>
        </div>
      )}

      {reviewable && (
        <div className="flex gap-2 pt-2 border-t">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onAction("approve")}
                  data-testid="review-approve-btn">
            <CheckCircle2 size={14} className="mr-1" /> Approve
          </Button>
          <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={() => onAction("needs_correction")} data-testid="review-send-back-btn">
            <RotateCcw size={14} className="mr-1" /> Send Back
          </Button>
          <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => onAction("reject")} data-testid="review-reject-btn">
            <XCircle size={14} className="mr-1" /> Reject
          </Button>
        </div>
      )}
    </div>
  );
}

function ActionDialog({ product, action, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = {
    approve: { title: "Approve product", desc: "This will publish the product to complaint registration and (if any warranty months are set) upsert a warranty rule.", btn: "Approve", color: "bg-emerald-600 hover:bg-emerald-700 text-white", required: false },
    needs_correction: { title: "Send back for correction", desc: "CCR will be asked to correct and resubmit. Remarks are mandatory.", btn: "Send Back", color: "bg-orange-600 hover:bg-orange-700 text-white", required: true },
    reject: { title: "Reject product", desc: "Product will be marked rejected and won't appear in complaint registration. Remarks are mandatory.", btn: "Reject", color: "bg-rose-600 hover:bg-rose-700 text-white", required: true },
  }[action];

  const submit = async () => {
    if (meta.required && !remarks.trim()) {
      toast.error("Remarks are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/review`, { action, remarks: remarks || null });
      toast.success(`Product ${meta.btn.toLowerCase()}d`);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`action-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle>{meta.title} — {product.name}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Remarks {meta.required && <span className="text-rose-600">*</span>}
          </Label>
          <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    placeholder={meta.required ? "Why is this being sent back / rejected?" : "Optional comments"}
                    data-testid="action-dialog-remarks" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className={meta.color} onClick={submit} disabled={busy} data-testid="action-dialog-submit">
            {busy ? "Saving…" : meta.btn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800">{value || "—"}</div>
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
